// ** React Imports
import {ReactNode, useMemo} from 'react'
import * as yup from 'yup';

// ** Next Imports
import {Link, useLocation, useNavigate} from 'react-router-dom'

// ** MUI Components
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import {styled} from '@mui/material/styles'
import MuiCard, {CardProps} from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import MuiFormControlLabel, {FormControlLabelProps} from '@mui/material/FormControlLabel'

// ** Icon Imports

// ** Layout Import
import BlankLayout from 'src/@core/layouts/BlankLayout'

import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import {
    AuthorizationRequest,
    AuthorizationRequestFields,
    AuthorizationResponse,
    AuthorizationResponseFields
} from "../../types/user/authData";
import {HttpAuth} from "../../http/user/httpAuth";
import {userStorage} from "../../utils/localStorage";

import {ControlledTextField} from "../../components/forms";
import {ControlledPasswordField} from "../../components/forms/ControlledPasswordField";
import useAxios from "../../hooks/useAxios";
import {HttpAxiosRequest} from "../../http/httpAxiosBase";
import GoogleSignInButton from "./components/GoogleLogin";

// ** Styled Components
const Card = styled(MuiCard)<CardProps>(({theme}) => ({
    [theme.breakpoints.up('sm')]: {width: '28rem'}
}))

const LinkStyled = styled(Link)(({theme}) => ({
    fontSize: '0.875rem',
    textDecoration: 'none',
    color: theme.palette.primary.main
}))

const FormControlLabel = styled(MuiFormControlLabel)<FormControlLabelProps>(({theme}) => ({
    '& .MuiFormControlLabel-label': {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary
    }
}))

enum LogInFormFields {
    Mail = 'vchMail',
    Password = 'vchPassword'
}

type LogInFormData = {
    [LogInFormFields.Mail]: string,
    [LogInFormFields.Password]: string
}

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {fetchData} = useAxios<AuthorizationResponse>();

    const {search} = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search])

    const logInFormSchema = yup.object().shape({
        [LogInFormFields.Mail]: yup.string().email('Mail inválido').required('Campo obligatorio'),
        [LogInFormFields.Password]: yup.mixed().required('Campo obligatorio')
    })
    const {
        control,
        handleSubmit
    } = useForm<LogInFormData>({
        resolver: yupResolver(logInFormSchema),
    });

    function onLoginResponse(response: AuthorizationResponse) {
        HttpAxiosRequest.refreshToken(response[AuthorizationResponseFields.AuthToken]);

        const groupsIds: number[] = response[AuthorizationResponseFields.GroupIds].split(",").map((x: string) => parseInt(x));
        const profilesIds: number[] = response[AuthorizationResponseFields.ProfileIds].split(",").map((x: string) => parseInt(x));

        userStorage.save(
            response[AuthorizationResponseFields.UserId],
            response[AuthorizationRequestFields.Mail],
            groupsIds,
            profilesIds,
            response[AuthorizationResponseFields.PersonRoleId],
            response[AuthorizationResponseFields.PasswordChangeDaysRemaining],
            response[AuthorizationResponseFields.Lastname],
            response[AuthorizationResponseFields.DefaultLanguage],
            response[AuthorizationResponseFields.IsBankGroup],
            response[AuthorizationResponseFields.IsInternalGroup],
            response[AuthorizationResponseFields.AuthToken],
            response[AuthorizationResponseFields.IsAdmin],
            response[AuthorizationResponseFields.CodCargo],
            response[AuthorizationResponseFields.Cargo],
            response[AuthorizationResponseFields.CanCreateNewMessage]
        );
        navigate(location.state?.from || '/home')
    }

    function onLogIn(data: LogInFormData) {
        const request: AuthorizationRequest = {
            mail: data.vchMail,
            password: data.vchPassword,
            ipClient: ''
        };

        fetchData(
            () => HttpAuth.authenticateUser(request),
            true
        )
            .then(onLoginResponse)
    }


    /*useEffect(() => {
      HttpAuth.ping().then();
      if (params.get('fromLogout') === 'true') {
        userStorage.removeFromStorage();
      }
    }, [])*/

    return (
        <Grid container justifyContent={'center'} alignItems={'center'} sx={{height: '100vh !important'}}>
            <Card sx={{zIndex: 1}}>
                <CardContent sx={{p: theme => `${theme.spacing(12, 9, 7)} !important`}}>
                    <Box sx={{mb: 6}}>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2
                        }}>
                            <Typography variant='h5' sx={{fontWeight: 600, mb: 1.5}}>
                                Bienvenido ! 👋🏻
                            </Typography>
                            <img
                                src={`/images/logo_nueva_italia.svg`}
                                height={'54'}
                                alt={`logo-img`}
                            />
                        </Box>
                        {
                            window.LOGIN_ITAPTEC &&
                            <Typography variant='body2'>Por favor, ingrese sus datos para acceder al
                                sistema.</Typography>

                        }
                    </Box>
                    <form noValidate autoComplete='off' onSubmit={handleSubmit(onLogIn)}>
                        {
                            window.LOGIN_ITAPTEC &&
                            <>
                                <ControlledTextField control={control} name={LogInFormFields.Mail} autoFocus fullWidth
                                                     id='email' label='Email' sx={{mb: 4}}/>
                                <ControlledPasswordField control={control} name={LogInFormFields.Password}
                                                         label={'Contraseña'}
                                                         fullWidth/>
                                <Box
                                    sx={{
                                        mb: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    {/*<FormControlLabel control={<Checkbox />} label='Recordar mi usuario' />*/}
                                    {/*<LinkStyled to='/forgot-password'>¿Olvidó su contraseña? Click aquí para*/}
                                    {/*    recuperarla</LinkStyled>*/}
                                </Box>
                                <Button fullWidth size='large' type='submit' variant='contained' sx={{mb: 3}}>
                                    Ingresar
                                </Button>

                            </>
                        }

                        {
                            (window.LOGIN_ITAPTEC && window.LOGIN_GOOGLE) &&
                            <Divider sx={{my: theme => `${theme.spacing(5)} !important`}}>o</Divider>
                        }

                        {
                            window.LOGIN_GOOGLE &&
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>

                                <GoogleSignInButton
                                    onLoginResponse={onLoginResponse}
                                />
                            </Box>
                        }

                    </form>
                </CardContent>
            </Card>
        </Grid>
    )
}

LoginPage.getLayout = (page: ReactNode) => <BlankLayout>{page}</BlankLayout>

LoginPage.guestGuard = true

export default LoginPage
